import { apiRequest, ApiError } from '../utils/apiClient';

/**
 * GET /api/entitlements: what the current viewer may use.
 *
 * Contract: blueprint §4.4, served by backend/server/routes/entitlements.js
 * (BE-1). Optional auth: no token is a guest; a bad token is a 401, which
 * apiClient answers by refreshing the session once. `?fresh=1` asks the server
 * to bypass its 60-second entitlement cache (after a purchase).
 *
 * Every field is normalised here, so a partial or older response can never
 * crash a consumer: unknown values fall back to the least-privileged reading.
 */

/** Why the market console is locked for this viewer. */
export type MarketAccessReason = 'AUTH_REQUIRED' | 'NO_ENTITLEMENT' | 'CONTACT_COMPANY_ADMIN';

const MARKET_ACCESS_REASONS: readonly MarketAccessReason[] = [
  'AUTH_REQUIRED',
  'NO_ENTITLEMENT',
  'CONTACT_COMPANY_ADMIN',
];

/** Whether the viewer is a professional account, and which signal said so. */
export interface ProfessionalStatus {
  value: boolean;
  /** Server-side signal, e.g. 'role' or 'activity'. Null when not professional. */
  via: string | null;
}

/** The «مؤشرات السوق» add-on as the viewer can buy it. */
export interface MarketAddonOffer {
  slug: string;
  /** isActive && priceSar != null && !isQuoteBased, computed by the server. */
  purchasable: boolean;
  priceSar: number | null;
  priceCoins: number | null;
  durationDays: number | null;
}

/** The one real example the demo preview is pinned to (env-configured server side). */
export interface MarketDemoExample {
  cityId: string;
  /** Display name resolved from the market snapshot, never hard-coded. */
  city: string;
  placeId: string;
  district: string;
  segment: string;
  rentType: string;
  propertyType: string;
  areaM2: number | null;
  budget: number | null;
}

export interface MarketDemo {
  /** False when the example place is missing from the current snapshot. */
  available: boolean;
  example: MarketDemoExample | null;
}

export interface MarketToolsEntitlement {
  /** False while the kill switch MARKET_ACCESS_ENFORCED=false is set: everyone gets the full console. */
  enforced: boolean;
  entitled: boolean;
  /** How an entitled viewer got access: 'admin' | 'subscription' | 'addon'. */
  via: string | null;
  /** Null when entitled. */
  reason: MarketAccessReason | null;
  hasSubscription: boolean;
  subscriptionQualifies: boolean;
  /** ISO date the viewer's add-on access ends, when `via` is 'addon' and it has an end. */
  addonEndsAt: string | null;
  /** Null when the server sends no add-on summary. */
  addon: MarketAddonOffer | null;
  demo: MarketDemo;
}

export interface Entitlements {
  signedIn: boolean;
  professional: ProfessionalStatus;
  marketTools: MarketToolsEntitlement;
}

/** One load: the answer, and whether it came from the server (`ok`) or a fallback. */
export interface EntitlementsLoad {
  data: Entitlements;
  ok: boolean;
}

export interface EntitlementsRequestOptions {
  /** Bypass the server's 60-second cache. */
  fresh?: boolean;
  signal?: AbortSignal;
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function asText(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

function asTextOrNull(value: unknown): string | null {
  return asText(value) || null;
}

function asNumberOrNull(value: unknown): number | null {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

function lockedReason(signedIn: boolean): MarketAccessReason {
  return signedIn ? 'NO_ENTITLEMENT' : 'AUTH_REQUIRED';
}

/**
 * The least-privileged answer, used when the server cannot be reached: not
 * professional (beyond what the session shows), locked, no demo. Consumers
 * should offer a retry rather than present this as the viewer's real state.
 */
export function guestEntitlements(signedIn: boolean): Entitlements {
  return {
    signedIn,
    professional: { value: false, via: null },
    marketTools: {
      enforced: true,
      entitled: false,
      via: null,
      reason: lockedReason(signedIn),
      hasSubscription: false,
      subscriptionQualifies: false,
      addonEndsAt: null,
      addon: null,
      demo: { available: false, example: null },
    },
  };
}

/**
 * The answer for a backend that predates /api/entitlements (it 404s). Such a
 * backend has no market gate either, so the console is open: enforced=false.
 */
export function ungatedEntitlements(signedIn: boolean): Entitlements {
  const base = guestEntitlements(signedIn);
  return {
    ...base,
    marketTools: { ...base.marketTools, enforced: false, reason: null },
  };
}

function normalizeAddon(raw: unknown): MarketAddonOffer | null {
  const addon = asRecord(raw);
  if (!addon) return null;
  const slug = asText(addon.slug);
  if (!slug) return null;
  return {
    slug,
    purchasable: addon.purchasable === true,
    priceSar: asNumberOrNull(addon.priceSar),
    priceCoins: asNumberOrNull(addon.priceCoins),
    durationDays: asNumberOrNull(addon.durationDays),
  };
}

function normalizeDemo(raw: unknown): MarketDemo {
  const demo = asRecord(raw);
  const example = asRecord(demo?.example);
  const cityId = asText(example?.cityId);
  const placeId = asText(example?.placeId);
  // Without both ids the tools cannot be pre-filled, so there is no demo.
  if (!demo || demo.available !== true || !example || !cityId || !placeId) {
    return { available: false, example: null };
  }
  return {
    available: true,
    example: {
      cityId,
      city: asText(example.city),
      placeId,
      district: asText(example.district),
      segment: asText(example.segment),
      rentType: asText(example.rentType),
      propertyType: asText(example.propertyType),
      areaM2: asNumberOrNull(example.areaM2),
      budget: asNumberOrNull(example.budget),
    },
  };
}

/** Coerce a raw `data` payload into a complete Entitlements object. */
export function normalizeEntitlements(raw: unknown, signedInHint: boolean): Entitlements {
  const data = asRecord(raw);
  const signedIn = typeof data?.signedIn === 'boolean' ? data.signedIn : signedInHint;

  const professionalRaw = asRecord(data?.professional);
  const professionalValue = professionalRaw?.value === true;

  const market = asRecord(data?.marketTools);
  const entitled = market?.entitled === true;
  const rawReason = asText(market?.reason) as MarketAccessReason;
  const reason = entitled
    ? null
    : MARKET_ACCESS_REASONS.includes(rawReason)
      ? rawReason
      : lockedReason(signedIn);

  return {
    signedIn,
    professional: {
      value: professionalValue,
      via: professionalValue ? asTextOrNull(professionalRaw?.via) : null,
    },
    marketTools: {
      // Anything but an explicit false is read as enforced (least privilege).
      enforced: market?.enforced !== false,
      entitled,
      via: entitled ? asTextOrNull(market?.via) : null,
      reason,
      hasSubscription: market?.hasSubscription === true,
      subscriptionQualifies: market?.subscriptionQualifies === true,
      addonEndsAt: entitled ? asTextOrNull(market?.addonEndsAt) : null,
      addon: normalizeAddon(market?.addon),
      demo: normalizeDemo(market?.demo),
    },
  };
}

/**
 * Fetch and normalise. Throws on any failure: an ApiError for HTTP and network
 * errors, a plain Error when a 200 carries no `data` object (for example an
 * HTML page served by a misrouted proxy).
 */
export async function fetchEntitlements(
  token?: string | null,
  options: EntitlementsRequestOptions = {}
): Promise<Entitlements> {
  const path = options.fresh ? '/entitlements?fresh=1' : '/entitlements';
  const response = await apiRequest<{ data?: unknown }>(path, {
    token: token || undefined,
    signal: options.signal,
    retries: 1,
  });
  const data = asRecord(asRecord(response)?.data);
  if (!data) {
    throw new Error('Malformed /entitlements response');
  }
  return normalizeEntitlements(data, Boolean(token));
}

/**
 * Fetch without throwing. `ok` is false when the answer is the guest-safe
 * fallback because the request failed. A 404 is a successful reading: a backend
 * without /api/entitlements predates the market gate, so nothing is gated.
 */
export async function loadEntitlements(
  token?: string | null,
  options: EntitlementsRequestOptions = {}
): Promise<EntitlementsLoad> {
  const signedIn = Boolean(token);
  try {
    return { ok: true, data: await fetchEntitlements(token, options) };
  } catch (error) {
    if (error instanceof ApiError && error.status === 404) {
      return { ok: true, data: ungatedEntitlements(signedIn) };
    }
    return { ok: false, data: guestEntitlements(signedIn) };
  }
}

/** The blueprint's entry point: never throws; on failure, the guest-safe fallback. */
export async function getEntitlements(
  token?: string | null,
  options: EntitlementsRequestOptions = {}
): Promise<Entitlements> {
  return (await loadEntitlements(token, options)).data;
}
